import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { ApiContext } from '@api/api';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import {
  AllSupplierNotificationFilter,
  CompanyModuleEnum,
  EntityTypeEnum,
  FeaturesEnum,
  ListSupplierNotificationPreviewOutDTO,
  Pageable,
  PermissionTypesEnum,
  ShipmentStateCodeEnum,
  LabelPreviewWithTypeOutDTO,
  SupplierNotificationGroupedFilterDTO,
  SupplierNotificationStatusEnum,
  SupplierNotificationTypeEnum,
  TableKeyEnum,
} from '@api/ringil-api.v2';
import { isNotBlank, isNotNullOrUndefined, isNullOrUndefined, useLocalStorage } from '@utils/utils';
import { dayjsFromApiPrague, dayjsFromApiUtc, getBrowserTimezone, stringDateTimeWithLocaleFromApiStringInTz } from '@utils/dateWithTz';
import { formatTimeslotInterval, isSomeOpenTimeslotsExists } from '@utils/timeslotDateTime';
import useNavigation from '@hooks/useNavigation.tsx';
import { snMergeDisabledReason } from '@app/pages/expedition/create/mergeEligibility.ts';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import { Link } from 'react-router-dom';
import { useCreateMultiEditSupplierNotificationSidebar } from '@app/pages/customer/supplier-notification/sidebars/MultiEditSupplierNotificationSidebar.tsx';
import { useContructCreateMultiDemandComponent } from './dialogs/CreateMultiDemand';
import Badge from '@components/ringil3/Badges/Badge.tsx';
import SupplierNotificationMoreButtons from '@app/pages/customer/supplier-notification/SupplierNotificationMoreButtons.tsx';
import SNCreateExpeditionAttachmentDialog from '@components/obsolete/DuplicateExpedition/SNCreateExpeditionAttachmentDialog.tsx';
import { useMediaQuery } from 'react-responsive';
import BreadCrumbComponent from '@components/obsolete/BreadCrumbComponent/BreadCrumbComponent.tsx';
import Heading from '@components/ringil3/Heading/Heading.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import CalendarSvg from '@icons/calendar.svg?react';
import BuildingSvg from '@icons/BuildingSvg.tsx';
import WarningSvg from '@icons/Warning.tsx';
import TruckSvg from '@icons/TruckSvg';
import PlusIconSvg from '@icons/PlusIcon';
import AlertCircle from '@icons/AlertCircle';
import dayjs from 'dayjs';
import clsx from 'clsx';
import { isEntityOwner } from '@app/pages/customer/supplier-notification/utils.ts';
import COTimezone from '@components/FigmaComponents/COTimezone.tsx';
import SecuredArea from '@components/security/SecuredArea';
import { useSecuredFlag } from '@components/security/useSecuredFlag';
import { NO_LABEL_FILTER_VALUE } from '@components/framework/tags/LabelsTag.tsx';
import useResponsiblePersons from '@hooks/responsible-persons/responsible-persons.tsx';
import useCountries from '@hooks/use-countries/useCountries.tsx';
import useFolders from '@hooks/use-folders/useFolders.tsx';
import { truncateFileName } from '@utils/fileUtils.ts';
import ExpandableLabelWrapper from '@components/RingilDataTable/ExpandableLabelWrapper.tsx';
import ExpandableInfoTag from '@components/RingilDataTable/ExpandableInfoTag.tsx';
import ExpandableInfoAllTag from '@components/RingilDataTable/ExpandableInfoAllTag.tsx';
import AttachmentLink from '@components/RingilDataTable/AttachmentLink.tsx';
import CountryFlag from '@components/framework/CountryFlag/CountryFlag.tsx';
import ExpeditionBadge from '@components/RingilComponents/badges/ExpeditionBadge.tsx';
import GroupDemandBadge from '@components/RingilComponents/badges/GroupDemandBadge.tsx';
import SvgChevron from '@icons/chevron.svg?react';
import SvgLink from '@icons/link.svg?react';
import SvgNumber from '@icons/number.svg?react';
import SvgFile from '@icons/file-inverted.svg?react';
import SvgCargo from '@icons/cargo.svg?react';
import RingilDataTable, {
  ColumnConfig,
  DateTimeFormatType,
  FilterConfig,
  FilterType,
  MassActionConfig,
  OptionType,
  PagedData,
} from '@components/RingilDataTable/RingilDataTable.tsx';
import RingilDataTableV3, { TableViewsConfig } from '@components/RingilDataTable/v3/RingilDataTableV3.tsx';
import LegacyVersionSwitch from '@components/framework/LegacyVersionSwitch.tsx';
import { BetaSurface } from '@hooks/useBetaVersion/betaSurfaces.ts';
import SupplierNotificationDetail from '@app/pages/customer/supplier-notification/SupplierNotificationDetail.tsx';
import SupplierNotificationDetailDap from '@app/pages/dap/SupplierNotificationDetailDap.tsx';
import { buildSnFields } from './v3/snFields';
import { SN_DEFAULT_COLUMNS, SN_FIELD_GROUPS, SN_PRESETS } from './v3/snFieldKeys';

// Lightweight shape the multi-demand / multi-edit flows consume.
interface SelectedObject {
  id: string;
  isGroupDemandCreated: boolean;
  isExpeditionCreated: boolean;
  hasTimeslots: boolean;
  shipmentState: ShipmentStateCodeEnum;
  snType: SupplierNotificationTypeEnum;
}

// Per-tag expansion keys for the "further information" column.
type InfoTagKey = 'all' | 'shipmentNumber' | 'cargo' | 'attachments' | 'internalNote';

export const ResponsiveSupplierNotificationList: React.FC = () => {
  const { tr } = useTranslationLgs();
  const navigation = useNavigation();
  const { loggedUser } = useContext(AuthenticatedUserContext);
  const {
    listSupplierNotification,
    getUserSupplierNotificationFilters,
    saveUserSupplierNotificationFilters,
    deleteUserFilter,
    downloadAttachment,
    listAllLabelsForCompany,
    lovAllCompaniesByInheritModule,
  } = useContext(ApiContext);

  const isMobile = useMediaQuery({ maxWidth: 1024 });

  const snListRwFlag = useSecuredFlag('supplier-notification-list-rw', {
    kind: 'action',
    description: 'Manage supplier notifications in the list (mass actions, row actions)',
    permissions: [PermissionTypesEnum.PERMISSION_SN_LIST_RW],
  });
  const snCreateFlag = useSecuredFlag('supplier-notification-create', {
    kind: 'action',
    description: 'Create supplier notification',
    permissions: [PermissionTypesEnum.PERMISSION_SN_EFXX_CREATE],
  });
  const snExportFlag = useSecuredFlag('supplier-notification-export', {
    kind: 'action',
    description: 'Export supplier notifications (generic export API)',
    features: [FeaturesEnum.GENERIC_EXPORT_API],
    permissions: [PermissionTypesEnum.PERMISSION_SUPP_NOTIFICATION_EXPORT],
  });
  const snModuleFlag = useSecuredFlag('supplier-notification-module', {
    kind: 'feature',
    description: 'Supplier notification module is enabled for the company',
    modules: [{ module: CompanyModuleEnum.SUPPLIER_NOTIFICATION, inherited: false }],
  });
  const snDistributionFlag = useSecuredFlag('supplier-notification-distribution', {
    kind: 'action',
    description: 'Create distribution / carrier selection for supplier notifications',
    permissions: [PermissionTypesEnum.PERMISSION_EXPEDITION_CREATE_DISTRIBUTION_RW],
  });
  const snCreateExpeditionFlag = useSecuredFlag('supplier-notification-create-expedition', {
    kind: 'action',
    description: 'Create an expedition from a supplier notification',
    modules: [{ module: CompanyModuleEnum.SUPPLIER_NOTIFICATION, inherited: false }],
    permissions: [PermissionTypesEnum.PERMISSION_SN_LIST_RW],
  });

  const mergeSupplierNotificationsFlag = useSecuredFlag('feature-sn-merge-to-expedition', {
    kind: 'feature',
    description: 'Merging several supplier notifications into one shipment is enabled for the company',
    features: [FeaturesEnum.SN_MERGE_TO_EXPEDITION],
  });

  const attachmentNameFilterFlag = useSecuredFlag('supplier-notification-attachment-name-filter', {
    kind: 'visibility',
    description: 'Filter supplier notifications by attachment name',
    permissions: [
      PermissionTypesEnum.PERMISSION_SN_EFXX_DETAIL_TAB_SN_ATTACHMENTS_READ,
      PermissionTypesEnum.PERMISSION_SN_EFXX_DETAIL_TAB_SN_ATTACHMENTS_RW,
      PermissionTypesEnum.PERMISSION_SN_DXX_DETAIL_TAB_SN_ATTACHMENTS_READ,
      PermissionTypesEnum.PERMISSION_SN_DXX_DETAIL_TAB_SN_ATTACHMENTS_RW,
    ],
  });

  const showDistribution = snDistributionFlag.granted;

  const [expandedTags, setExpandedTags] = useLocalStorage<Record<InfoTagKey, boolean>>('SUPPLIER_NOTIFICATION_LIST_EXPANDED_TAGS', {
    all: false,
    shipmentNumber: false,
    cargo: false,
    attachments: false,
    internalNote: false,
  });
  const [expandedLabels, setExpandedLabels] = useLocalStorage<boolean>('SUPPLIER_NOTIFICATION_LIST_EXPANDED_LABELS', false);

  const [createExpeditionForSnId, setCreateExpeditionForSnId] = useState<string | null>(null);

  const tableRef = useRef<{ reloadData: () => void; selectedObjects: ListSupplierNotificationPreviewOutDTO[]; clear: () => void }>(null);

  const { MultiEditSupplierNotificationSidebar, setVisible: setVisibleMultiEdit } = useCreateMultiEditSupplierNotificationSidebar(() => {
    tableRef.current?.clear();
    tableRef.current?.reloadData();
  });
  const { CreateMultiDemandDialog, setSelectedSupplierNotifications } = useContructCreateMultiDemandComponent();

  const toggleTag = (key: InfoTagKey) => {
    setExpandedTags(prev => ({ ...prev, [key]: !prev?.[key] }));
  };

  const setAllTagsExpanded = (expanded: boolean) => {
    setExpandedTags({ all: expanded, shipmentNumber: expanded, cargo: expanded, attachments: expanded, internalNote: expanded });
  };

  // --- Filter option sources -------------------------------------------------
  // RD-2966: adaptive filtering - once the list has loaded, dropdown options come from the list response
  // (each computed from all the other active filters); the static sources below only cover the first render.
  const [groupedFilter, setGroupedFilter] = useState<SupplierNotificationGroupedFilterDTO | null>(null);
  const countries = useCountries();
  const responsiblePersons = useResponsiblePersons();
  const folders = useFolders(EntityTypeEnum.SUPPLIER_NOTIFICATION);
  const [companyLabels, setCompanyLabels] = useState<LabelPreviewWithTypeOutDTO[]>([]);
  const [suppliers, setSuppliers] = useState<{ id: string; name: string }[]>([]);

  useEffect(() => {
    listAllLabelsForCompany(d => {
      const labels = d?.find(t => t.entityType === EntityTypeEnum.SUPPLIER_NOTIFICATION)?.labels ?? [];
      setCompanyLabels(labels.map(l => ({ ...l, entityType: EntityTypeEnum.SUPPLIER_NOTIFICATION })));
    });
  }, []);

  useEffect(() => {
    lovAllCompaniesByInheritModule(CompanyModuleEnum.SUPPLIER_NOTIFICATION, d => {
      setSuppliers(d ?? []);
    });
  }, []);

  const labelOptions: OptionType[] = useMemo(
    () => [
      { label: tr('generic.withoutLabel', 'Without label'), value: NO_LABEL_FILTER_VALUE, active: true },
      ...(groupedFilter?.labels ?? companyLabels).map(l => ({
        label: l.labelValue,
        value: l.labelId,
        active: true,
        icon: <span className="inline-block w-[14px] h-[14px] rounded-sm" style={{ backgroundColor: l.labelColor }} />,
      })),
    ],
    [tr, groupedFilter, companyLabels],
  );
  const supplierOptions: OptionType[] = useMemo(
    () => (groupedFilter?.suppliers ?? suppliers).map(c => ({ label: c.name, value: c.id, active: true })),
    [groupedFilter, suppliers],
  );
  const countryOptions: OptionType[] = useMemo(
    () => (groupedFilter?.supplierLocationCountries ?? countries ?? []).map(c => ({ label: c.name, value: c.code, active: true, icon: <CountryFlag code={c.code} /> })),
    [groupedFilter, countries],
  );
  const responsiblePersonOptions: OptionType[] = useMemo(
    () => (groupedFilter?.responsiblePersons ?? responsiblePersons ?? []).map(p => ({ label: p.name, value: p.id, active: true })),
    [groupedFilter, responsiblePersons],
  );
  const folderOptions: OptionType[] = useMemo(
    () => (groupedFilter?.folders ?? folders ?? []).map(f => ({ label: f.name, value: f.id, active: true })),
    [groupedFilter, folders],
  );
  // Entity-owner (customer) options derived from the logged-in user's modules,
  // mirroring InputEntityOwner.
  const entityOwnerOptions: OptionType[] = useMemo(
    () =>
      groupedFilter
        ? groupedFilter.customers.map(c => ({ label: c.name, value: c.id, active: true }))
        : (loggedUser?.modules ?? [])
            .filter(m => m.companyModule === CompanyModuleEnum.SUPPLIER_NOTIFICATION)
            .map(m => ({
              label: m.inheritingModule ? m.owner.companyName : loggedUser.company,
              value: m.inheritingModule ? m.owner.companyId : loggedUser.companyId,
              active: true,
            })),
    [groupedFilter, loggedUser],
  );
  const typeOptions: OptionType[] = useMemo(
    () =>
      [
        { label: 'DXX', value: SupplierNotificationTypeEnum.DXX, active: true },
        { label: 'E/FXX', value: SupplierNotificationTypeEnum.EFXX, active: true },
      ].filter(o => !groupedFilter || groupedFilter.supplierNotificationType.includes(o.value)),
    [groupedFilter],
  );

  // --- Data loading ----------------------------------------------------------
  const normalizeSupplierNotificationFilter = (value?: AllSupplierNotificationFilter): AllSupplierNotificationFilter =>
    ({
      ...(value ?? {}),
      userTimezoneId: getBrowserTimezone(),
    } as AllSupplierNotificationFilter);

  const loadDataApi = (filter: AllSupplierNotificationFilter, pageable: Pageable, onSuccess: (pagedData: PagedData<ListSupplierNotificationPreviewOutDTO>) => void) => {
    listSupplierNotification(pageable, filter, d => {
      setGroupedFilter(d.groupedFilter ?? null);
      onSuccess({ data: d.data, total: d.total, page: d.page, size: d.size });
    });
  };

  // --- Attachments -----------------------------------------------------------
  const handleFileClick = (attachmentId: string, fileName: string) => {
    downloadAttachment(attachmentId, (data: { mimeType: string; data: string }) => {
      const linkSource = `data:${data.mimeType};base64,${data.data}`;
      const downloadLink = document.createElement('a');
      downloadLink.href = linkSource;
      downloadLink.download = fileName;
      downloadLink.click();
    });
  };

  /**
   * The shipment a row actually ends up on. A notification merged into another shipment had its own
   * one canceled to get there, so the badge follows the merge — linking to the original would send
   * the user to a dead shipment. Neither present means no badge, as before.
   */
  const linkedExpeditionOf = (row: ListSupplierNotificationPreviewOutDTO) => row.mergedToExpedition ?? row.expeditionCreated;

  // --- Column renderers ------------------------------------------------------
  const renderApplicationId = (row: ListSupplierNotificationPreviewOutDTO) => (
    <div className="flex flex-col">
      <span data-testid={`sn-row-${row.applicationId}`} data-application-id={row.applicationId} data-supplier-notification-id={row.id} style={{ display: 'none' }} />
      <div className="font-medium flex items-center gap-2">
        <Link to={navigation.createNavigationLink(navigation.urlFunctions.createSupplierNotificationDetail(row.id, row.supplierNotificationType))}>
          <span className="font-medium">{row.applicationId}</span>
        </Link>
      </div>
      <SecuredArea
        id="row-responsible-person" instance={row.applicationId}
        visible={[{ type: 'data', label: 'responsiblePersonName present', satisfied: isNotBlank(row.responsiblePersonName) }]}
        label="Responsible person"
      >
        <div className="text-gray-600 text-sm mt-1">{row.responsiblePersonName}</div>
      </SecuredArea>
      <div className="mt-2 flex gap-1" data-testid={`sn-row-${row.applicationId}-snType`}>
        <SecuredArea
          id="row-sn-type-dxx" instance={row.applicationId}
          visible={[{ type: 'data', label: 'type === DXX', satisfied: row.supplierNotificationType === SupplierNotificationTypeEnum.DXX }]}
          label="DXX badge"
        >
          <Badge label={tr('SupplierNotificationList.dxx', 'DXX')} />
        </SecuredArea>
        <SecuredArea
          id="row-sn-type-efxx" instance={row.applicationId}
          visible={[{ type: 'data', label: 'type === EFXX', satisfied: row.supplierNotificationType === SupplierNotificationTypeEnum.EFXX }]}
          label="E/FXX badge"
        >
          <Badge label={tr('SupplierNotificationList.efxx', 'E/FXX')} />
        </SecuredArea>
      </div>
      <SecuredArea
        id="row-labels" instance={row.applicationId}
        visible={[{ type: 'data', label: 'labels.length > 0', satisfied: !!row.labels && row.labels.length > 0 }]}
        label="Labels"
      >
        <ExpandableLabelWrapper labels={row.labels} isExpanded={expandedLabels} onToggle={() => setExpandedLabels(v => !v)} />
      </SecuredArea>
      {/* Linked expedition badge (requires the SN module); otherwise fall back to the plain application id. */}
      <SecuredArea
        id="row-expedition-badge" instance={row.applicationId}
        visible={[snModuleFlag, { type: 'data', label: 'linked expedition present', satisfied: !!linkedExpeditionOf(row)?.expeditionApplicationId }]}
        label="Linked expedition badge" description="Badge linking to the shipment created from the notification"
      >
        <ExpeditionBadge expeditionId={linkedExpeditionOf(row)?.expeditionId} expeditionAppId={linkedExpeditionOf(row)?.expeditionApplicationId} />
      </SecuredArea>
      {!snModuleFlag.granted && linkedExpeditionOf(row)?.expeditionApplicationId && <div>{linkedExpeditionOf(row)?.expeditionApplicationId}</div>}
    </div>
  );

  const renderLocationAndDate = (row: ListSupplierNotificationPreviewOutDTO) => (
    <div className="flex flex-col gap-4">
      <SecuredArea
        id="row-supplier-location" instance={row.applicationId}
        visible={[{ type: 'data', label: 'supplier location present', satisfied: isNotBlank(row.supplierName) }]}
        label="Supplier location"
      >
        <div className="flex flex-col">
          <div className="text-[#616161]" data-testid={`sn-row-${row.applicationId}-location-consignorName`}>
            {row.supplierName}
          </div>
          <div className="text-[#616161] text-sm" data-testid={`sn-row-${row.applicationId}-location-consignorLocation`}>
            <CountryFlag code={row.supplierLocationCountry} /> {row.supplierLocationCountry}, {row.supplierLocationCity}
          </div>
          <SecuredArea
            id="row-supplier-timeslot" instance={row.applicationId}
            visible={[{ type: 'data', label: 'supplier delivery slots present', satisfied: row.supplierDeliverySlots.dayWithInterval.length > 0 }]}
            label="Supplier timeslot"
          >
            <div>
              <div className="text-[#616161] flex items-center gap-2" data-testid={`sn-row-${row.applicationId}-location-consignorTimeslot`}>
                <CalendarSvg className={'text-content-secondary'} /> {formatTimeslotInterval(row.supplierDeliverySlots)}
              </div>
              <COTimezone dataTestId={`sn-row-${row.applicationId}-location-consignorTimezone`} timezoneId={row.supplierDeliverySlots.timezoneId} />
            </div>
          </SecuredArea>
        </div>
      </SecuredArea>
      <SecuredArea
        id="row-customer-location" instance={row.applicationId}
        visible={[{ type: 'data', label: 'customer location present', satisfied: isNotBlank(row.customerName) }]}
        label="Customer location"
      >
        <div className="flex flex-col">
          <div className="text-[#616161]" data-testid={`sn-row-${row.applicationId}-location-consigneeName`}>
            {row.customerName}
          </div>
          <div className="text-[#616161] text-sm" data-testid={`sn-row-${row.applicationId}-location-consigneeLocation`}>
            <CountryFlag code={row.customerLocationCountry} /> {row.customerLocationCountry}, {row.customerLocationCity}
          </div>
          <SecuredArea
            id="row-customer-timeslot" instance={row.applicationId}
            visible={[{ type: 'data', label: 'customer delivery slots present', satisfied: row.customerDeliverySlots.dayWithInterval.length > 0 }]}
            label="Customer timeslot"
          >
            <div>
              <div className="text-[#616161] flex items-center gap-2" data-testid={`sn-row-${row.applicationId}-location-consigneeTimeslot`}>
                <CalendarSvg className={'text-content-secondary'} /> {formatTimeslotInterval(row.customerDeliverySlots)}
              </div>
              <COTimezone dataTestId={`sn-row-${row.applicationId}-location-consigneeTimezone`} timezoneId={row.customerDeliverySlots.timezoneId} />
            </div>
          </SecuredArea>
        </div>
      </SecuredArea>
    </div>
  );

  const renderSupplierAndResponsible = (row: ListSupplierNotificationPreviewOutDTO) => (
    <div className="flex flex-col">
      <div className="text-[#616161] flex items-center gap-2" data-testid={`sn-row-${row.applicationId}-consignorName`}>
        <BuildingSvg />
        {row.supplierName}
      </div>
      <SecuredArea
        id="row-partner-company-note" instance={row.applicationId}
        visible={[{ type: 'data', label: 'partnerCompanyNote present', satisfied: isNotBlank(row.partnerCompanyNote) }]}
        label="Partner company note"
      >
        {/* The note is free text that can be one long unbroken word: overflow-wrap anywhere lets it
            break, max-w-full keeps the w-fit box from widening the column past the cell. */}
        <div className="inline-flex items-center gap-2 px-2 py-1.5 border border-solid border-[#ffeee5] rounded text-[#343333] text-sm w-fit max-w-full min-w-0 [overflow-wrap:anywhere] mt-2 min-h-[32px]">
          {row.partnerCompanyNote}
        </div>
      </SecuredArea>
    </div>
  );

  const renderExpedition = (row: ListSupplierNotificationPreviewOutDTO) => (
    <div className="flex flex-col">
      {/* Waiting for payment */}
      <SecuredArea
        id="row-waiting-payment" instance={row.applicationId}
        visible={[
          {
            type: 'ownership',
            label: 'logged user is entity owner',
            satisfied: isEntityOwner(loggedUser, row.entityOwnerCompanyId),
            description: 'Owner only: shown only to the company the notification is for',
            byDesign: true,
          },
          { type: 'data', label: 'shipment state WAITING', satisfied: row.shipmentStateCode === ShipmentStateCodeEnum.WAITING },
        ]}
        label="Waiting for payment"
        description="Warning on a notification still waiting for confirmation, shown to the notification owner"
      >
        <div className="py-1 rounded inline-flex gap-1 items-center">
          <WarningSvg />
          <span>{row.shipmentStateName}</span>
        </div>
      </SecuredArea>

      <SecuredArea
        id="row-without-timeslots" instance={row.applicationId}
        visible={[
          {
            type: 'ownership',
            label: 'logged user is entity owner',
            satisfied: isEntityOwner(loggedUser, row.entityOwnerCompanyId),
            description: 'Owner only: shown only to the company the notification is for',
            byDesign: true,
          },
          { type: 'data', label: 'no open supplier timeslots', satisfied: !isSomeOpenTimeslotsExists(row.supplierDeliverySlots) },
        ]}
        label="Without timeslots"
        description="Warning on a notification with no open loading timeslots, shown to the notification owner"
      >
        <div className="py-1 rounded inline-flex gap-1 items-center">
          <WarningSvg />
          <span>{tr(`SupplierNotificationList.withoutTimeslots`, `Without timeslots`)}</span>
        </div>
      </SecuredArea>

      {/* Group demand data */}
      <SecuredArea
        id="row-group-demand" instance={row.applicationId}
        visible={[
          {
            type: 'ownership',
            label: 'logged user is entity owner',
            satisfied: isEntityOwner(loggedUser, row.entityOwnerCompanyId),
            description: 'Owner only: shown only to the company the notification is for',
            byDesign: true,
          },
          { type: 'data', label: 'group demand created, no expedition yet', satisfied: !!row.groupDemandCreated && !row.expeditionCreated },
        ]}
        label="Group demand progress"
        description="Progress of the group demand created from the notification, shown to the notification owner"
      >
        <div className="flex flex-col gap-2">
          {row.groupDemandCreated?.validTill &&
            !row.expeditionCreated?.expeditionApplicationId &&
            (() => {
              const gd = row.groupDemandCreated;
              // Atomic check — use new fields only when BOTH utcEnd AND timezoneId are present
              const hasNew = isNotNullOrUndefined(gd.utcEnd) && isNotNullOrUndefined(gd.timezoneId);
              const endDayjs = hasNew ? dayjsFromApiUtc(gd.utcEnd) : dayjsFromApiPrague(gd.validTill);
              const displayTz = hasNew ? gd.timezoneId : loggedUser?.defaultTimezoneId ?? 'Europe/Prague';
              const displayString = hasNew ? gd.utcEnd : gd.validTill;
              const isExpired = endDayjs.isBefore(dayjs.utc());
              const displayValue = stringDateTimeWithLocaleFromApiStringInTz(displayString, displayTz);
              return isExpired ? (
                <div className="flex items-center gap-1 text-[#E4002B]">
                  <AlertCircle width={16} height={16} />
                  <span>
                    {tr('SupplierNotificationList.expired', 'Expired')} {displayValue}
                  </span>
                </div>
              ) : (
                <div className="text-[#182230]">
                  {tr('SupplierNotificationList.validityTill', 'Validity till')} {displayValue}
                </div>
              );
            })()}

          {!row.groupDemandCreated?.assignedCarriersCount && (
            <div className="flex items-center gap-1">
              <WarningSvg />
              <span>{tr('SupplierNotificationList.carriersAreNotAssigned', 'No requests sent to carriers')}</span>
            </div>
          )}

          {row.groupDemandCreated?.assignedCarriersCount > 0 &&
            (() => {
              const gd = row.groupDemandCreated;
              const percent = gd.repliedCarriersCount > 0 && gd.assignedCarriersCount > 0 ? (gd.repliedCarriersCount / gd.assignedCarriersCount) * 100 : 10;
              return (
                <div className="flex items-center gap-2 whitespace-nowrap w-full">
                  <span className="text-sm">
                    {gd.repliedCarriersCount} {tr('SupplierNotificationList.responses', 'odpov.')}
                  </span>
                  <div className="bg-[#e5e7eb] h-2 w-[90px] relative shrink-0 overflow-hidden">
                    <div
                      className="h-2 transition-[width] duration-300"
                      style={{ width: `${percent > 100 ? 100 : percent}%`, background: gd.repliedCarriersCount > 0 ? '#22c55e' : '#ef4444' }}
                    />
                  </div>
                  <span className="text-sm">
                    {tr('SupplierNotificationList.from', 'z')} {gd.assignedCarriersCount}
                  </span>
                </div>
              );
            })()}
        </div>
      </SecuredArea>
      {/* Group demand label */}
      <SecuredArea
        id="row-group-demand-badge" instance={row.applicationId}
        visible={[{ type: 'data', label: 'group demand name present', satisfied: !!row.groupDemandCreated?.groupDemandName }]}
        label="Group demand badge"
      >
        <GroupDemandBadge
          groupDemandId={row.groupDemandCreated?.groupDemandId}
          groupDemandName={row.groupDemandCreated?.groupDemandName}
          linked={isEntityOwner(loggedUser, row.entityOwnerCompanyId)}
        />
      </SecuredArea>
    </div>
  );

  const renderAdditionalInfo = (row: ListSupplierNotificationPreviewOutDTO) => {
    const hasShipmentNumber = isNotBlank(row.shipmentNumber);
    const hasCargo = !!row.cargo && row.cargo.length > 0;
    const hasAttachments = !!row.attachmentInfo && row.attachmentInfo.attachmentSize > 0;
    const hasInternalNote = isNotBlank(row.internalNote);
    const hasAdditionalInfo = hasShipmentNumber || hasCargo || hasAttachments || hasInternalNote;

    return (
      <div className="flex flex-wrap gap-2 items-start">
        <SecuredArea
          id="row-tag-all" instance={row.applicationId}
          visible={[{ type: 'data', label: 'any additional info available', satisfied: hasAdditionalInfo }]}
          label="Expand-all tag"
        >
          <ExpandableInfoAllTag
            id={`all_${row.id}`}
            dataTestId={`sn-row-${row.applicationId}-additional-all`}
            onClick={() => setAllTagsExpanded(!expandedTags?.['all'])}
            tooltipText={expandedTags?.['all'] ? tr('expandableInfoTag.collapseAll', 'Collapse all') : tr('expandableInfoTag.expandAll', 'Expand all')}
            icon={<SvgChevron className={clsx('text-content-brand', expandedTags?.['all'] && 'rotate-180')} />}
          />
        </SecuredArea>

        <SecuredArea
          id="row-tag-shipment-number" instance={row.applicationId}
          visible={[{ type: 'data', label: 'shipmentNumber present', satisfied: hasShipmentNumber }]}
          label="Shipment number tag"
        >
          <ExpandableInfoTag
            id={`shipmentNumber_${row.id}`}
            dataTestId={`sn-row-${row.applicationId}-additional-shipmentNumber`}
            expanded={!!expandedTags?.['shipmentNumber']}
            onClick={() => toggleTag('shipmentNumber')}
            label={tr('SupplierNotificationList.shipmentNumber', 'Shipment')}
            text={row.shipmentNumber}
            icon={<SvgNumber />}
          />
        </SecuredArea>

        <SecuredArea
          id="row-tag-cargo" instance={row.applicationId}
          visible={[{ type: 'data', label: 'cargo present', satisfied: hasCargo }]}
          label="Cargo tag"
        >
          <ExpandableInfoTag
            id={`cargo_${row.id}`}
            dataTestId={`sn-row-${row.applicationId}-additional-cargo`}
            expanded={!!expandedTags?.['cargo']}
            onClick={() => toggleTag('cargo')}
            label={tr('SupplierNotificationList.cargo', 'Cargo: ')}
            text={row.cargo?.map(c => `${c.name}: ${c.amount}`).join(', ')}
            icon={<SvgCargo />}
            count={row.cargo?.reduce((sum, c) => sum + (c.amount ?? 0), 0)}
          />
        </SecuredArea>

        <SecuredArea
          id="row-tag-attachments" instance={row.applicationId}
          visible={[{ type: 'data', label: 'attachmentSize > 0', satisfied: hasAttachments }]}
          label="Attachments tag"
        >
          <ExpandableInfoTag
            id={`attachments_${row.id}`}
            dataTestId={`sn-row-${row.applicationId}-additional-attachments`}
            expanded={!!expandedTags?.['attachments']}
            onClick={() => toggleTag('attachments')}
            icon={<SvgLink className={expandedTags?.['attachments'] ? 'text-content-brand' : ''} />}
            label={tr('SupplierNotificationList.attachments', 'Attachments: ')}
            count={row.attachmentInfo?.attachmentSize ?? 0}
            text={row.attachmentInfo?.attachments?.map(a => truncateFileName(a.name)).join(' | ')}
            textComponent={
              <span className="font-medium">
                {row.attachmentInfo?.attachments?.map((attachment, idx) => (
                  <React.Fragment key={attachment.id}>
                    <AttachmentLink id={attachment.id} name={attachment.name} onActivate={(id, fileName) => handleFileClick(id, fileName)} maxLength={15} />
                    {idx < row.attachmentInfo.attachments.length - 1 && <span className="text-content-tertiary">{' | '}</span>}
                  </React.Fragment>
                ))}
              </span>
            }
          />
        </SecuredArea>

        <SecuredArea
          id="row-tag-internal-note" instance={row.applicationId}
          visible={[{ type: 'data', label: 'internalNote present', satisfied: hasInternalNote }]}
          label="Internal note tag"
        >
          <ExpandableInfoTag
            id={`internalNote_${row.id}`}
            dataTestId={`sn-row-${row.applicationId}-additional-internalNote`}
            expanded={!!expandedTags?.['internalNote']}
            onClick={() => toggleTag('internalNote')}
            label={tr('SupplierNotificationList.internalNote', 'Internal Note: ')}
            text={row.internalNote}
            icon={<SvgFile />}
          />
        </SecuredArea>
      </div>
    );
  };

  const expeditionDuplicationQueryParams = new URLSearchParams({
    routeAndParameters: 'true',
    cargoAndOrders: 'true',
    documents: 'false',
    labels: 'false',
    internalInfo: 'true',
    carrierSelection: showDistribution ? 'true' : 'false',
    carrierNote: 'true',
  });

  const renderActions = (row: ListSupplierNotificationPreviewOutDTO) => (
    <div className="flex flex-col gap-2">
      <SecuredArea
        id="row-create-expedition" instance={row.applicationId}
        visible={[
          snCreateExpeditionFlag,
          {
            // States of the notification, not "nothing to show": as `data` it would never reach the AI assistant.
            type: 'state',
            label: 'EFXX without expedition/demand, not waiting, open timeslots',
            description:
              'Offered only for an EFXX notification with no shipment or group demand yet, not waiting for confirmation, and with open loading and unloading timeslots',
            satisfied:
              isNullOrUndefined(row.expeditionCreated) &&
              isNullOrUndefined(row.groupDemandCreated) &&
              row.shipmentStateCode !== ShipmentStateCodeEnum.WAITING &&
              row.supplierNotificationType === SupplierNotificationTypeEnum.EFXX &&
              isSomeOpenTimeslotsExists(row.supplierDeliverySlots) &&
              isSomeOpenTimeslotsExists(row.customerDeliverySlots),
          },
        ]}
        label="Create expedition from SN" description="Create a shipment from the notification"
      >
        <button
          className="relative w-8 h-8 bg-white border border-solid border-[#d9d9d9] rounded flex items-center justify-center cursor-pointer p-0 shadow-[0px_4px_4px_0px_rgba(254,80,0,0.15)] hover:border-[#ff6b00]"
          title={tr('SupplierNotificationList.vytvoritPrepravu', 'Vytvořit přepravu')}
          onClick={() => {
            if ((row.attachmentInfo?.attachmentSize ?? 0) > 0) {
              setCreateExpeditionForSnId(row.id);
            } else {
              navigation.navigate(
                navigation.urlFunctions.createExpeditionFromEntity(row.id, EntityTypeEnum.SUPPLIER_NOTIFICATION, {
                  queryParams: expeditionDuplicationQueryParams,
                }),
              );
            }
          }}
        >
          <TruckSvg width={16} height={16} />
          <div className="absolute -right-1.5 -bottom-1.5 w-[18px] h-[18px] bg-[#fe5000] rounded-full flex items-center justify-center border-2 border-solid border-white">
            <PlusIconSvg />
          </div>
        </button>
      </SecuredArea>
      {/* isDAP: DXX rows open in the DAP detail (see the snType switch in the detail route), where duplication is not offered. */}
      <SupplierNotificationMoreButtons
        supplierNotificationId={row.id}
        supplierNotificationApplicationId={row.applicationId}
        supplierLocationName={row.supplierLocationName}
        archived={row.isArchived}
        isEntityOwner={isEntityOwner(loggedUser, row.entityOwnerCompanyId)}
        isDAP={row.supplierNotificationType !== SupplierNotificationTypeEnum.EFXX}
        isCanceled={row.supplierNotificationStatus === SupplierNotificationStatusEnum.CANCELED}
        actionsSecured={snListRwFlag}
        buttonSize={'small'}
        buttonMode={'table'}
        dataTestId={`sn-moreButton-${row.applicationId}`}
        onChange={() => tableRef.current?.reloadData()}
        onCanceledSupplierNotification={() => tableRef.current?.reloadData()}
      />
    </div>
  );

  // --- Column / filter / mass-action config ----------------------------------
  const columnConfig: ColumnConfig<ListSupplierNotificationPreviewOutDTO>[] = [
    {
      id: 'applicationId',
      field: 'applicationId',
      header: 'SupplierNotificationList.id',
      headerDefault: 'ID',
      sortable: true,
      isDefaultSort: true,
      order: 1,
      render: renderApplicationId,
    },
    {
      id: 'expedition',
      field: 'expeditionCreatedPreview.expeditionApplicationId',
      header: 'SupplierNotificationList.expeditionAppId',
      headerDefault: 'Expedition',
      sortable: true,
      order: 2,
      render: renderExpedition,
    },
    { id: 'loadingUnloading', header: 'SupplierNotificationList.loadingUnloading', headerDefault: 'Loading/Unloading', order: 3, render: renderLocationAndDate },
    { id: 'supplier', header: 'SupplierNotificationList.supplierName', headerDefault: 'Supplier', order: 4, render: renderSupplierAndResponsible },
    { id: 'furtherInformation', header: 'SupplierNotificationList.furtherInformation', headerDefault: 'Further information', order: 5, render: renderAdditionalInfo },
    { id: 'actions', header: '', headerDefault: '', width: '120px', order: 6, render: row => renderActions(row) },
  ];

  const filtersConf: FilterConfig[] = useMemo(
    () => [
      {
        filterType: FilterType.InputText,
        placeholder: tr('SupplierNotificationList.applicationId', 'Application ID'),
        value: ['applicationId'],
        dataTestId: 'sn-filter-applicationId',
      },
      { filterType: FilterType.InputText, placeholder: tr('SupplierNotificationList.groupDemandConnectName', 'Group demand name'), value: ['groupDemandConnectName'] },
      { filterType: FilterType.InputText, placeholder: tr('SupplierNotificationList.loadingLocationName', 'Loading location name'), value: ['loadingLocationName'] },
      {
        filterType: FilterType.Dropdown,
        placeholder: tr('SupplierNotificationList.supplierLocationCountry', 'Loading country'),
        value: ['supplierLocationCountry'],
        options: countryOptions,
      },
      { filterType: FilterType.InputText, placeholder: tr('SupplierNotificationList.supplierLocationCity', 'Loading city'), value: ['supplierLocationCity'] },
      {
        filterType: FilterType.Calendar,
        placeholder: tr('SupplierShipments.loadingDateFromISO', 'From'),
        value: ['loadingDateFromISO'],
        dateTimeFormat: DateTimeFormatType.ISOString,
      },
      {
        filterType: FilterType.Calendar,
        placeholder: tr('SupplierShipments.loadingDateToISO', 'Till'),
        value: ['loadingDateToISO'],
        dateTimeFormat: DateTimeFormatType.ISOString,
      },
      {
        filterType: FilterType.MultiSelect,
        placeholder: tr('SupplierNotificationList.responsiblePersons', 'Responsible persons'),
        value: ['responsiblePersonIds'],
        options: responsiblePersonOptions,
      },
      { filterType: FilterType.Dropdown, placeholder: tr('SupplierNotificationList.customerId', 'Customer'), value: ['customerId'], options: entityOwnerOptions },
      { filterType: FilterType.Dropdown, placeholder: tr('SupplierNotificationList.supplierId', 'Supplier'), value: ['supplierId'], options: supplierOptions },
      {
        filterType: FilterType.InputText,
        placeholder: tr('SupplierNotificationList.expeditionConnectApplicationId', 'Connected expedition ID'),
        value: ['expeditionConnectApplicationId'],
      },
      { filterType: FilterType.InputText, placeholder: tr('SupplierNotificationList.shipmentNumber', 'Shipment number'), value: ['shipmentNumber'] },
      {
        filterType: FilterType.InputText,
        placeholder: tr('SupplierNotificationDetail.cmrNumber', 'CMR Number'),
        value: ['cmrNumber'],
        dataTestId: 'sn-filter-cmrNumber',
      },
      { filterType: FilterType.InputText, placeholder: tr('CreateSupplierNotification.blNumber', 'BL Number'), value: ['blNumber'], dataTestId: 'sn-filter-blNumber' },
      {
        filterType: FilterType.InputText,
        placeholder: tr('CreateSupplierNotification.awbNumber', 'AWB Number'),
        value: ['awbNumber'],
        dataTestId: 'sn-filter-awbNumber',
      },
      { filterType: FilterType.MultiSelect, placeholder: tr('InputLabel.labels', 'Labels'), value: ['labelIds'], options: labelOptions },
      { filterType: FilterType.MultiSelect, placeholder: tr('AllExpedition.folderIds', 'Folder'), value: ['folderIds'], options: folderOptions },
      {
        filterType: FilterType.Dropdown,
        placeholder: tr('SupplierNotificationList.type', 'Type'),
        value: ['supplierNotificationType'],
        options: typeOptions,
      },
      { filterType: FilterType.InputText, placeholder: tr('SupplierNotificationList.referenceOrderId', 'Reference order'), value: ['referenceOrderId'] },
      ...(attachmentNameFilterFlag.granted
        ? [
            {
              filterType: FilterType.InputText,
              placeholder: tr('generic.attachmentName', 'Attachment name'),
              value: ['attachmentName'],
              dataTestId: 'sn-filter-attachmentName',
            } as FilterConfig,
          ]
        : []),
      { filterType: FilterType.Checkbox, placeholder: tr('SupplierNotificationList.showOnlyArchived', 'Show only archived'), value: ['showArchived'] },
      { filterType: FilterType.Checkbox, placeholder: tr('SupplierNotificationList.onlyWithoutExpedition', 'Show without expedition'), value: ['onlyWithoutExpedition'] },
    ],
    [tr, countryOptions, responsiblePersonOptions, entityOwnerOptions, supplierOptions, labelOptions, folderOptions, typeOptions, attachmentNameFilterFlag.granted],
  );

  // Beta SPLIT_VIEW: the v3 table with tabs, its columns built from the field catalogue.
  // Rebuilt on every render so the fields see the current expanded tags and labels.
  const snFields = buildSnFields({
    tr,
    detailLink: row => navigation.createNavigationLink(navigation.urlFunctions.createSupplierNotificationDetail(row.id, row.supplierNotificationType)),
    isOwner: row => isEntityOwner(loggedUser, row.entityOwnerCompanyId),
    timezoneId: loggedUser?.defaultTimezoneId ?? 'Europe/Prague',
    flags: { snModule: snModuleFlag },
    infoTags: { isExpanded: key => !!expandedTags?.[key], toggle: toggleTag, setAll: setAllTagsExpanded },
    labels: { expanded: expandedLabels, toggle: () => setExpandedLabels(v => !v) },
    onAttachmentClick: handleFileClick,
  });
  const snViews: TableViewsConfig<ListSupplierNotificationPreviewOutDTO> = {
    tableKey: TableKeyEnum.SUPPLIER_NOTIFICATION_LIST,
    catalogue: snFields,
    presets: SN_PRESETS,
    defaultColumns: SN_DEFAULT_COLUMNS,
    defaultSort: { sortField: 'applicationId', sortDirection: 'DESC' },
    trailing: [
      {
        id: 'actions',
        header: '',
        headerDefault: '',
        width: '60px',
        sticky: 'right',
        // The v3 cell clips its content, and the create button's "+" badge sticks out 6px right and below: room for it.
        render: row => <div className="pr-1.5 pb-1.5">{renderActions(row)}</div>,
      },
    ],
    // Stored as the tab's name when it is created, so it is translated once, in the user's language at that moment.
    firstViewName: tr('generic.all', 'All'),
    // Old saved filters become tabs once, on the first visit; best-effort and silent, as on the expedition list.
    importLegacy: () =>
      new Promise(resolve =>
        getUserSupplierNotificationFilters(
          data => resolve((data?.filters ?? []).map(saved => ({ name: saved.name, filter: (saved.data ?? {}) as Record<string, unknown> }))),
          null,
          { onError: () => resolve([]), onValidationFailed: () => resolve([]) },
        ),
      ),
    groups: SN_FIELD_GROUPS,
  };

  const selectedRows = (): ListSupplierNotificationPreviewOutDTO[] => tableRef.current?.selectedObjects ?? [];

  const toSelectedObject = (row: ListSupplierNotificationPreviewOutDTO): SelectedObject => ({
    id: row.id,
    isGroupDemandCreated: isNotNullOrUndefined(row.groupDemandCreated),
    isExpeditionCreated: isNotNullOrUndefined(row.expeditionCreated),
    hasTimeslots: isSomeOpenTimeslotsExists(row.supplierDeliverySlots) && isSomeOpenTimeslotsExists(row.customerDeliverySlots),
    shipmentState: row.shipmentStateCode,
    snType: row.supplierNotificationType,
  });

  const massActionConfig: MassActionConfig<ListSupplierNotificationPreviewOutDTO>[] = [
    {
      label: tr('SupplierNotificationList.multiDemand', 'Multi demand'),
      onClick: () => setSelectedSupplierNotifications(selectedRows().map(toSelectedObject)),
      disabled: selected =>
        selected.length === 0 ||
        selected.some(
          r =>
            r.supplierNotificationType === SupplierNotificationTypeEnum.DXX ||
            isNotNullOrUndefined(r.groupDemandCreated) ||
            isNotNullOrUndefined(r.expeditionCreated) ||
            r.shipmentStateCode === ShipmentStateCodeEnum.WAITING ||
            !(isSomeOpenTimeslotsExists(r.supplierDeliverySlots) && isSomeOpenTimeslotsExists(r.customerDeliverySlots)),
        ),
    },
    {
      label: tr('SupplierNotificationList.multiEdit', 'Multi edit'),
      onClick: () => setVisibleMultiEdit(true),
      disabled: selected => selected.length === 0,
    },
    {
      label: tr('SupplierNotificationList.mergeSupplierNotifications', 'Merge into one shipment'),
      onClick: () => navigation.navigate(navigation.urlFunctions.createMergedExpeditionFromSNs(selectedRows().map(row => row.id))),
      secured: () => ({ id: 'mass-action-sn-merge', label: 'Merge into one shipment', description: 'Merge the selected notifications into one shipment', visible: mergeSupplierNotificationsFlag }),
      // The backend takes 1-10 ids and enforces the same eligibility rules; greying the action out
      // with the offending notifications named saves the user a round trip. Both the greying-out and
      // its explanation come from the same rule, so they cannot drift.
      disabled: selected => snMergeDisabledReason(selected, tr) !== undefined,
      disabledReason: selected => snMergeDisabledReason(selected, tr),
    },
  ];

  const renderMobileCard = (row: ListSupplierNotificationPreviewOutDTO, checkbox?: JSX.Element | null) => (
    <div className="bg-white p-4 pb-6 shadow-[0_1px_0_0_rgba(229,231,235,1)] -mx-4 px-4 relative z-10">
      <div className="flex items-start gap-3">
        {checkbox}
        <div className="flex-grow">{renderApplicationId(row)}</div>
      </div>
      <div className="mt-3 border-t border-gray-100 pt-3">{renderExpedition(row)}</div>
      <div className="mt-3 border-t border-gray-100 pt-3">{renderLocationAndDate(row)}</div>
      <div className="mt-3 border-t border-gray-100 pt-3">{renderSupplierAndResponsible(row)}</div>
      <div className="mt-3 border-t border-gray-100 pt-3">{renderAdditionalInfo(row)}</div>
      <div className="mt-3 border-t border-gray-100 pt-3">{renderActions(row)}</div>
    </div>
  );

  if (isNullOrUndefined(loggedUser)) return <></>;

  return (
    <>
      <div className="font-['DM Sans']">
        <BreadCrumbComponent items={[{ label: tr(`SupplierNotificationList.supplierNotifications`, `Supplier notifications`) }]} />
        <div className="flex items-center">
          <Heading title={tr(`SupplierNotificationList.supplierNotifications`, `Supplier notifications`)} headingVariant={'h1'} />
          <SecuredArea id="supplier-notification-create-button" visible={snCreateFlag} label="Create supplier notification button" description="Button that creates a new supplier notification">
            <RingilButton
              mode="link"
              label={tr(`SupplierNotificationList.createSupplierNotification`, `Create supplier notification`)}
              icon={<i className="pi pi-plus" />}
              onClick={() => navigation.navigate(navigation.urlFunctions.createSupplierNotificationCreate())}
            />
          </SecuredArea>
        </div>
      </div>

      {/* Beta SPLIT_VIEW: the same list on the compact v3 table. Both get one set of props. */}
      <LegacyVersionSwitch
        surface={BetaSurface.SPLIT_VIEW}
        labels={{
          toNew: tr('SupplierNotificationList.switchToNewTable', 'Switch to new table'),
          toLegacy: tr('SupplierNotificationList.switchToLegacyTable', 'Switch to legacy table'),
        }}
      >
        {showLegacy => {
          const tableProps = {
            filtersConf,
            loadDataFromApi: loadDataApi,
            filterName: 'supplier_notification_list',
            getSavedFilters: getUserSupplierNotificationFilters as never,
            saveFilters: saveUserSupplierNotificationFilters as never,
            deleteSavedFilter: deleteUserFilter,
            massActionConfig: snModuleFlag.granted && snListRwFlag.granted ? massActionConfig : undefined,
            renderMobileCard: isMobile ? renderMobileCard : undefined,
            normalizeFilter: normalizeSupplierNotificationFilter,
            exportConfig: { entityType: EntityTypeEnum.SUPPLIER_NOTIFICATION, entityLabel: 'Supplier notifications', secured: snExportFlag },
          };
          return showLegacy ? (
            <RingilDataTable ref={tableRef} columnConfig={columnConfig} {...tableProps} />
          ) : (
            <RingilDataTableV3
              ref={tableRef}
              views={snViews}
              enableReload
              infiniteScroll
              // The split layouts (profile menu → page layout) show the open notification's detail beside or below the list;
              // a DXX one in its DAP detail, as its own page does.
              split={{
                renderDetail: (row, { onChanged }) =>
                  row.supplierNotificationType === SupplierNotificationTypeEnum.DXX ? (
                    <SupplierNotificationDetailDap key={row.id} supplierNotificationId={row.id} embedded onChanged={onChanged} />
                  ) : (
                    <SupplierNotificationDetail key={row.id} supplierNotificationId={row.id} embedded onChanged={onChanged} />
                  ),
              }}
              {...tableProps}
            />
          );
        }}
      </LegacyVersionSwitch>

      <CreateMultiDemandDialog
        onComplete={() => {
          tableRef.current?.clear();
          tableRef.current?.reloadData();
        }}
      />
      <MultiEditSupplierNotificationSidebar
        supplierNotificationIds={selectedRows().map(t => t.id)}
        supplierNotificationTypes={[...new Set(selectedRows().map(t => t.supplierNotificationType))]}
        containsGroupDemand={selectedRows().some(r => isNotNullOrUndefined(r.groupDemandCreated))}
        containsExpedition={selectedRows().some(r => isNotNullOrUndefined(r.expeditionCreated))}
        containsWaiting={selectedRows().some(r => r.shipmentStateCode === ShipmentStateCodeEnum.WAITING)}
        showDistribution={showDistribution}
      />
      <SNCreateExpeditionAttachmentDialog
        visible={createExpeditionForSnId !== null}
        onCancel={() => setCreateExpeditionForSnId(null)}
        supplierNotificationId={createExpeditionForSnId ?? ''}
      />
    </>
  );
};
